import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import Snake from '../games/Snake';
import Pong from '../games/Pong';
import TicTacToe from '../games/TicTacToe';
import FlappyBird from '../games/FlappyBird';
import Chess from '../games/Chess';

function useLockScroll(isActive) {
  useEffect(() => {
    if (!isActive) return
    
    const preventScroll = (e) => {
      const scrollKeys = [
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Space', 'PageUp', 'PageDown', 'Home', 'End', 'Tab'
      ]
      if (scrollKeys.includes(e.code)) {
        e.preventDefault()
      }
    }
    
    window.addEventListener('keydown', preventScroll, { passive: false })
    return () => window.removeEventListener('keydown', preventScroll)
  }, [isActive])
}

function GameLauncher({ title, color, children, icon }) {
  const [launched, setLaunched] = useState(false)
  const containerRef = useRef(null)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])
  
  useLockScroll(launched)

  if (!launched) {
    return (
      <motion.div
        onClick={() => setLaunched(true)}
        onTouchEnd={() => setLaunched(true)}
        whileHover={{ scale: isMobile ? 1 : 1.01 }}
        whileTap={{ scale: 0.99 }}
        style={{
          width: '100%',
          height: isMobile ? '200px' : '400px',
          borderRadius: '12px',
          border: `1px solid ${color}44`,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          gap: isMobile ? '0.75rem' : '1.5rem',
          position: 'relative',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        <motion.div
          animate={{ scale: [1, 1.4, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 3, repeat: Infinity }}
          style={{
            position: 'absolute',
            width: isMobile ? '150px' : '300px',
            height: isMobile ? '150px' : '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color}33, transparent)`,
            pointerEvents: 'none'
          }}
        />
        <span style={{ fontSize: isMobile ? '2.5rem' : '4rem' }}>{icon}</span>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: isMobile ? '1rem' : '1.3rem', fontWeight: 700, color: color, 
            fontFamily: 'monospace', letterSpacing: '0.1em', 
            textTransform: 'uppercase', marginBottom: '0.25rem' 
          }}>{title}</div>
          <div style={{ 
            fontSize: isMobile ? '0.65rem' : '0.8rem', color: 'rgba(255,255,255,0.4)', 
            letterSpacing: '0.2em', textTransform: 'uppercase' 
          }}>Tap to Play</div>
        </div>
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: color, boxShadow: `0 0 12px ${color}`
          }}
        />
      </motion.div>
    )
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      style={{ outline: 'none', position: 'relative', touchAction: 'none' }}
    >
      {children}
    </div>
  )
}

function GameSection({ accentColor, number, title, description, icon, gameComponent }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.15 })
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  return (
    <motion.section
      ref={ref}
      id={title.toLowerCase().replace(/\s+/g, '')}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        minHeight: isMobile ? 'auto' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '2rem 1rem' : '2rem',
        position: 'relative',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        gap: isMobile ? '1rem' : undefined,
      }}
    >
      {/* Ambient glow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 1.2, delay: 0.2 }}
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse 60% 50% at 50% 50%, ${accentColor}08 0%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      {/* Vertical accent line */}
      <motion.div
        initial={{ scaleY: 0, opacity: 0 }}
        animate={isInView ? { scaleY: 1, opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute',
          left: isMobile ? '0' : '2rem',
          top: isMobile ? '5%' : '15%',
          bottom: isMobile ? '5%' : '15%',
          width: isMobile ? '1px' : '2px',
          background: `linear-gradient(180deg, transparent, ${accentColor}, transparent)`,
          transformOrigin: 'top',
          boxShadow: isMobile ? 'none' : `0 0 12px ${accentColor}`,
          pointerEvents: 'none',
          opacity: isMobile ? 0.08 : 1,
        }}
      />

      {/* Number badge */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.05 }}
        style={{
          position: 'absolute',
          top: isMobile ? '0.5rem' : '2rem',
          left: isMobile ? '1rem' : '3rem',
          fontSize: '0.6rem',
          fontFamily: 'monospace',
          color: accentColor,
          letterSpacing: '0.15em',
          opacity: isMobile ? 0.3 : 0.7
        }}
      >
        {number} / 05
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute',
          fontSize: 'clamp(8rem, 25vw, 20rem)',
          fontWeight: 900,
          color: 'rgba(255,255,255,0.02)',
          userSelect: 'none',
          pointerEvents: 'none',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          lineHeight: 1,
          display: isMobile ? 'none' : 'block',
        }}
      >{number}</motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 50, filter: 'blur(10px)' }}
        animate={isInView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        style={{
          fontSize: 'clamp(2rem, 8vw, 6rem)',
          fontWeight: 900,
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
          color: accentColor,
          marginBottom: isMobile ? '0.25rem' : '0.5rem',
          textShadow: `0 0 40px ${accentColor}66`,
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}
      >{title}</motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.25 }}
        style={{
          color: 'rgba(255,255,255,0.4)',
          fontSize: isMobile ? '0.7rem' : '0.85rem',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: isMobile ? '1.5rem' : '2.5rem',
          fontFamily: 'monospace',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}
      >{description}</motion.p>

      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.95 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: '100%',
          maxWidth: isMobile ? '95vw' : '700px',
          position: 'relative',
          zIndex: 1,
          marginBottom: isMobile ? '1.5rem' : undefined,
        }}
      >
        <GameLauncher title={title} color={accentColor} icon={icon}>
          {gameComponent}
        </GameLauncher>
      </motion.div>
    </motion.section>
  )
}

export default function Games() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  return (
    <div style={{ background: '#050508', minHeight: '100vh' }}>

      {/* ARCADE Hero Section */}
      <section style={{
        height: isMobile ? '35vh' : '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '0 1rem',
      }}>
        <motion.h1
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          style={{
            fontSize: 'clamp(3rem, 15vw, 10rem)',
            fontWeight: 900,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            lineHeight: 0.9,
            color: 'white',
            margin: isMobile ? '0 0 1.5rem 0' : '0 0 3rem 0',
            textAlign: 'center',
          }}
        >
          ARCADE
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{ color: '#94a3b8', fontSize: 'clamp(0.9rem, 3vw, 1.5rem)', textAlign: 'center' }}
        >
          Take a break. Play some games.
        </motion.p>
      </section>

      {/* Game Sections */}
      <GameSection
        accentColor="#00d9ff"
        number="01"
        title="CHESS"
        description="Classic strategy — checkmate your opponent"
        icon="♟"
        gameComponent={<Chess />}
      />

      <GameSection
        accentColor="#22c55e"
        number="02"
        title="FLAPPY BIRD"
        description="Tap to fly — survive the pipes"
        icon={<img src="/flappy-bird-logo.png" alt="Flappy Bird" style={{width:'180px',height:'180px',objectFit:'contain'}} />}
        gameComponent={<FlappyBird />}
      />

      <GameSection
        accentColor="#00d9ff"
        number="03"
        title="SNAKE"
        description="Eat, grow, avoid yourself"
        icon={<img src="/snake-game-logo.png" alt="Snake" style={{width:'180px',height:'180px',objectFit:'contain'}} />}
        gameComponent={<Snake />}
      />

      <GameSection
        accentColor="#ff006e"
        number="04"
        title="PONG"
        description="AI opponent — first to 7 wins"
        icon={<img src="/ping-pong-logo.png" alt="Pong" style={{width:'180px',height:'180px',objectFit:'contain'}} />}
        gameComponent={<Pong />}
      />

      <GameSection
        accentColor="#d000ff"
        number="05"
        title="TIC TAC TOE"
        description="VS AI or a friend — 3 in a row wins"
        icon={<img src="/Tic-tac-toe-logo.png" alt="Tic Tac Toe" style={{width:'180px',height:'180px',objectFit:'contain'}} />}
        gameComponent={<TicTacToe />}
      />
    </div>
  );
}
